/**
 * GA4 Centralized Event Tracking Engine
 * Provides trackEvent(), trackPageView(), parameter sanitization, PII redaction, and deduplication.
 */

import { rawGtag } from './gtag';
import { getAnalyticsConsent } from './consent';
import { isValidEventName, sanitizeParameters, sanitizeUrl } from './sanitizer';
import { isDuplicateEvent, isDuplicatePageView } from './deduplication';
import type { TrackEventOptions, TrackPageViewOptions } from './types';

/**
 * Tracks an SPA Page View.
 * Prevents duplicates on re-renders, sanitizes sensitive query parameters.
 */
export function trackPageView(options: TrackPageViewOptions = {}): boolean {
  if (typeof window === 'undefined') return false;

  // 1. Consent check
  if (getAnalyticsConsent() === 'denied') {
    return false;
  }

  const path = options.path || window.location.pathname;
  const rawLocation = options.location || window.location.href;
  const title = options.title || document.title || 'Sewar Gourmet';
  const referrer = options.referrer || document.referrer || '';

  // 2. Prevent duplicate SPA pageviews
  if (isDuplicatePageView(path)) {
    return false;
  }

  // 3. Sanitize URL to remove sensitive queries and PII
  const sanitizedLocation = sanitizeUrl(rawLocation);
  const sanitizedReferrer = sanitizeUrl(referrer);

  const pageViewParams = {
    page_path: path,
    page_location: sanitizedLocation,
    page_title: title,
    page_referrer: sanitizedReferrer,
  };

  if (import.meta.env.DEV) {
    console.debug(`[GA4 PageView] 📄 ${path}`, pageViewParams);
  }

  rawGtag('event', 'page_view', pageViewParams);
  return true;
}

/**
 * Centralized trackEvent function.
 * - Validates event name syntax
 * - Strips PII and sensitive parameters
 * - Prevents duplicate rapid firings
 * - Respects analytics consent
 */
export function trackEvent(
  eventName: string,
  params: Record<string, any> = {},
  options: TrackEventOptions = {}
): boolean {
  if (typeof window === 'undefined') return false;

  // 1. Validate event name
  if (!isValidEventName(eventName)) {
    if (import.meta.env.DEV) {
      console.warn(`[GA4 Analytics] Invalid event name rejected: "${eventName}"`);
    }
    return false;
  }

  // 2. Consent check
  if (getAnalyticsConsent() === 'denied') {
    return false;
  }

  // 3. Sanitize parameters (strips PII, passwords, tokens, sensitive queries)
  const sanitizedParams = sanitizeParameters(params);

  // 4. Deduplication check
  if (!options.force && isDuplicateEvent(eventName, sanitizedParams, options.dedupeWindowMs)) {
    if (import.meta.env.DEV) {
      console.debug(`[GA4 Deduplication] Suppressed duplicate event: "${eventName}"`);
    }
    return false;
  }

  if (import.meta.env.DEV) {
    console.debug(`[GA4 Event] ⚡ ${eventName}`, sanitizedParams);
  }

  rawGtag('event', eventName, sanitizedParams);
  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// Domain-Specific E-Commerce & Restaurant Action Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Track category / menu list views
 */
export function trackViewItemList(listId: string, listName: string, itemsCount: number = 0) {
  return trackEvent('view_item_list', {
    item_list_id: String(listId),
    item_list_name: String(listName),
    items_count: itemsCount,
  });
}

/**
 * Track product / dish item selection
 */
export function trackSelectItem(item: {
  id: string | number;
  name: string;
  category?: string;
  price?: number;
  currency?: string;
  listName?: string;
}) {
  return trackEvent('select_item', {
    item_list_name: item.listName || 'Menu',
    items: [
      {
        item_id: String(item.id),
        item_name: item.name,
        item_category: item.category || 'General',
        price: item.price !== undefined ? Number(item.price) : undefined,
        currency: item.currency || 'IQD',
      },
    ],
  });
}

/**
 * Track viewing dish details (ProductModal or /product/:slug)
 */
export function trackViewItem(item: {
  id: string | number;
  name: string;
  category?: string;
  price?: number;
  currency?: string;
  hasDiscount?: boolean;
  calories?: number;
  prepTime?: number;
}) {
  return trackEvent('view_item', {
    currency: item.currency || 'IQD',
    value: item.price !== undefined ? Number(item.price) : 0,
    items: [
      {
        item_id: String(item.id),
        item_name: item.name,
        item_category: item.category || 'General',
        price: item.price !== undefined ? Number(item.price) : undefined,
        has_discount: Boolean(item.hasDiscount),
        calories: item.calories || undefined,
        prep_time: item.prepTime || undefined,
      },
    ],
  });
}

/**
 * Track search executed
 */
export function trackSearch(searchTerm: string, resultsCount: number = 0) {
  if (!searchTerm.trim()) return false;
  return trackEvent('search', {
    search_term: searchTerm.trim(),
    results_count: resultsCount,
  });
}

/**
 * Track search result selection
 */
export function trackSelectSearchResult(
  searchTerm: string,
  item: { id: string | number; name: string; category?: string }
) {
  return trackEvent('select_search_result', {
    search_term: searchTerm.trim(),
    item_id: String(item.id),
    item_name: item.name,
    item_category: item.category || 'General',
  });
}

/**
 * Track social / link sharing
 */
export function trackShare(
  method: 'whatsapp' | 'facebook' | 'telegram' | 'twitter' | 'copy_link' | string,
  contentType: 'product' | 'page' = 'product',
  itemId?: string,
  itemName?: string
) {
  return trackEvent('share', {
    method,
    content_type: contentType,
    item_id: itemId ? String(itemId) : undefined,
    item_name: itemName,
  });
}

/**
 * Track table reservation lead submitted via QuickBooking form (Key Event)
 */
export function trackReservationLead(data: {
  guests?: string;
  date?: string;
  time?: string;
  method?: 'form' | 'whatsapp';
}) {
  return trackEvent(
    'generate_lead',
    {
      guests_count: data.guests || '2',
      reservation_date: data.date,
      reservation_time: data.time,
      method: data.method || 'form',
    },
    { force: true }
  );
}

/**
 * Track WhatsApp reservation click (Key Event)
 */
export function trackWhatsAppBookingClick(source: 'quick_booking' | 'floating_button' | 'contact_page' = 'quick_booking') {
  return trackEvent(
    'whatsapp_booking_click',
    {
      source,
      destination: 'whatsapp',
    },
    { force: true }
  );
}

/**
 * Track direct phone call link click (Key Event)
 */
export function trackClickToCall(locationSource: string) {
  return trackEvent(
    'click_to_call',
    {
      location_source: locationSource,
    },
    { force: true }
  );
}

/**
 * Track customer review / rating submission (Key Event)
 */
export function trackReviewSubmission(
  ratings: {
    overall: number;
    staff?: number;
    service?: number;
    cleanliness?: number;
  },
  submissionMode: 'online' | 'offline_queue' = 'online'
) {
  return trackEvent(
    'submit_review',
    {
      rating_overall: ratings.overall,
      rating_staff: ratings.staff,
      rating_service: ratings.service,
      rating_cleanliness: ratings.cleanliness,
      submission_mode: submissionMode,
    },
    { force: true }
  );
}

/**
 * Track contact form submission (Key Event)
 */
export function trackContactFormSubmit() {
  return trackEvent(
    'form_submit_contact',
    {
      form_name: 'contact_us',
      status: 'success',
    },
    { force: true }
  );
}

/**
 * Track job application submission (Key Event)
 */
export function trackCareerFormSubmit(jobTitle?: string, department?: string) {
  return trackEvent(
    'form_submit_career',
    {
      form_name: 'career_application',
      job_title: jobTitle,
      job_department: department,
    },
    { force: true }
  );
}

/**
 * Track PWA install prompt interaction
 */
export function trackPwaInstall(action: 'prompt_shown' | 'accepted' | 'dismissed', platform?: string) {
  const eventName = action === 'accepted' ? 'pwa_install_accepted' : 'pwa_install_prompt_shown';
  return trackEvent(eventName, {
    action,
    platform: platform || (typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent) ? 'ios' : 'android_or_desktop'),
  });
}

/**
 * Track user changing language
 */
export function trackLanguageChange(selectedLanguage: string, previousLanguage?: string) {
  return trackEvent('change_language', {
    language_selected: selectedLanguage,
    previous_language: previousLanguage,
  });
}

/**
 * Track user changing currency
 */
export function trackCurrencyChange(selectedCurrency: string) {
  return trackEvent('change_currency', {
    currency_selected: selectedCurrency,
  });
}

/**
 * Track user switching theme (Dark / Light)
 */
export function trackThemeChange(themeMode: 'dark' | 'light') {
  return trackEvent('toggle_theme', {
    theme_mode: themeMode,
  });
}
